<template>
  <wide-modal variant="neutral" @close-modal="close">
    <spinner v-if="loading" />
    <div v-else-if="stage === 1">
      <div v-if="isPending" class="text-center">
        <button class="button-back mb-1" @click="showSummary">
          ⇠ Return To Summary
        </button>
      </div>
      <div class="spacing-vertical" />
      <my-bet-positions
        :game="position.game"
        :bet-type="position.betType || ''"
        :money-line-away="moneyLineAway"
        :money-line-home="moneyLineHome"
        :total-under="totalUnder"
        :total-over="totalOver"
        :spread-away="spreadAway"
        :spread-home="spreadHome"
        :money-line-1x2-away-yes="moneyLine1x2AwayYes"
        :money-line-1x2-away-no="moneyLine1x2AwayNo"
        :money-line-1x2-home-yes="moneyLine1x2HomeYes"
        :money-line-1x2-home-no="moneyLine1x2HomeNo"
        :money-line-1x2-draw-yes="moneyLine1x2DrawYes"
        :money-line-1x2-draw-no="moneyLine1x2DrawNo"
        :money-line-away-risk-win="moneyLineAwayRiskWin"
        :money-line-home-risk-win="moneyLineHomeRiskWin"
        :money-line-1x2-away-yes-risk-win="moneyLine1x2AwayYesRiskWin"
        :money-line-1x2-away-no-risk-win="moneyLine1x2AwayNoRiskWin"
        :money-line-1x2-home-yes-risk-win="moneyLine1x2HomeYesRiskWin"
        :money-line-1x2-home-no-risk-win="moneyLine1x2HomeNoRiskWin"
        :money-line-1x2-draw-yes-risk-win="moneyLine1x2DrawYesRiskWin"
        :money-line-1x2-draw-no-risk-win="moneyLine1x2DrawNoRiskWin"
        :moneyline-risk="moneylineRisk"
        :moneyline-1x2-risk="moneyline1x2Risk"
        :avg-odds-money-line="avgOddsMoneyLine"
        :avg-odds-money-line-1x2="avgOddsMoneyLine1x2"
        :avg-odds-spreads="avgOddsSpreads"
        :avg-odds-total="avgOddsTotal"
        :total-risk="totalRisk"
        :spread-risk="spreadRisk"
        :grouped-bets="groupedBets"
        :type="'active-wagers'"
        :league="position.game.league?.toLowerCase() || ''"
      />
    </div>
    <div v-else-if="stage === 0" class="text-center">
      <button class="button-back mb-1" @click="showSummary">
        ⇠ Return To Summary
      </button>
      <div class="py-3 mx-0 my-2 mt-md-4 mt-0 mb-md-4 games-not-found">
        Bets not Rendering in this view <br />
        Your wagers are safe, please Contact an Admin.
      </div>
    </div>
    <div v-else-if="stage === 2">
      <button class="button-back mb-1" @click="stage = 1">
        ⇠ Return To Summary
      </button>
      <double-bet-info-modal :bet="session" />
    </div>
  </wide-modal>
</template>

<script setup>
//CORE
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { useAPI } from '@/composables/useAPI';
import { emitter } from '../../../core/emitter';

//COMPONENTS
import Spinner from '@/components/global/Spinner.vue';
import MyBetPositions from '@/components/userBets/myPosition/MyPositionSingleBet.vue';
import DoubleBetInfoModal from '@/components/userBets/modals/DoubleBetInfoModal.vue';
import WideModal from '@/components/global/WideModal.vue';

//PROPS
const props = defineProps({
  position: {
    type: Object,
    required: true,
  },
  type: {
    type: String,
    default: 'user',
  },
  isPending: {
    type: Boolean,
    default: false,
  },
  player: {
    type: String,
    default: '',
  },
});

//CONST
const { get: getBetsForGame } = useAPI('user/getBetsForGame/');
const session = ref({});
const stage = ref(1);
const bets = ref([]);
const loading = ref(true);

//COMPUTED
const groupedBets = computed(() => {
  return groupByType(bets.value);
});

const moneyLineAway = computed(() => {
  return groupedBets.value.moneyline.away
    ? groupedBets.value.moneyline.away
    : [];
});

const moneyLineHome = computed(() => {
  return groupedBets.value.moneyline.home
    ? groupedBets.value.moneyline.home
    : [];
});

const totalUnder = computed(() => {
  return groupedBets.value.total.under
    ? groupTotalByNumber(groupedBets.value.total.under)
    : {};
});

const totalOver = computed(() => {
  return groupedBets.value.total.over
    ? groupTotalByNumber(groupedBets.value.total.over)
    : {};
});

const spreadAway = computed(() => {
  return groupedBets.value.spread.away
    ? groupSpreadByNumber(groupedBets.value.spread.away)
    : {};
});

const spreadHome = computed(() => {
  return groupedBets.value.spread.home
    ? groupSpreadByNumber(groupedBets.value.spread.home)
    : {};
});

const moneyLine1x2AwayYes = computed(() => {
  return groupedBets.value.moneyline1x2.awayYes
    ? groupedBets.value.moneyline1x2.awayYes
    : [];
});

const moneyLine1x2AwayNo = computed(() => {
  return groupedBets.value.moneyline1x2.awayNo
    ? groupedBets.value.moneyline1x2.awayNo
    : [];
});

const moneyLine1x2HomeYes = computed(() => {
  return groupedBets.value.moneyline1x2.homeYes
    ? groupedBets.value.moneyline1x2.homeYes
    : [];
});

const moneyLine1x2HomeNo = computed(() => {
  return groupedBets.value.moneyline1x2.homeNo
    ? groupedBets.value.moneyline1x2.homeNo
    : [];
});

const moneyLine1x2DrawYes = computed(() => {
  return groupedBets.value.moneyline1x2.drawYes
    ? groupedBets.value.moneyline1x2.drawYes
    : [];
});

const moneyLine1x2DrawNo = computed(() => {
  return groupedBets.value.moneyline1x2.drawNo
    ? groupedBets.value.moneyline1x2.drawNo
    : [];
});

const moneyLineAwayRiskWin = computed(() => {
  return groupedBets.value.moneyline.away
    ? moneyLineRiskWin(groupedBets.value.moneyline.away)
    : {};
});

const moneyLineHomeRiskWin = computed(() => {
  return groupedBets.value.moneyline.home
    ? moneyLineRiskWin(groupedBets.value.moneyline.home)
    : {};
});

const moneyLine1x2AwayYesRiskWin = computed(() => {
  return groupedBets.value.moneyline1x2.awayYes
    ? moneyLineRiskWin(groupedBets.value.moneyline1x2.awayYes)
    : {};
});

const moneyLine1x2AwayNoRiskWin = computed(() => {
  return groupedBets.value.moneyline1x2.awayNo
    ? moneyLineRiskWin(groupedBets.value.moneyline1x2.awayNo)
    : {};
});

const moneyLine1x2HomeYesRiskWin = computed(() => {
  return groupedBets.value.moneyline1x2.homeYes
    ? moneyLineRiskWin(groupedBets.value.moneyline1x2.homeYes)
    : {};
});

const moneyLine1x2HomeNoRiskWin = computed(() => {
  return groupedBets.value.moneyline1x2.homeNo
    ? moneyLineRiskWin(groupedBets.value.moneyline1x2.homeNo)
    : {};
});

const moneyLine1x2DrawYesRiskWin = computed(() => {
  return groupedBets.value.moneyline1x2.drawYes
    ? moneyLineRiskWin(groupedBets.value.moneyline1x2.drawYes)
    : {};
});

const moneyLine1x2DrawNoRiskWin = computed(() => {
  return groupedBets.value.moneyline1x2.drawNo
    ? moneyLineRiskWin(groupedBets.value.moneyline.drawNo)
    : {};
});

const moneylineRisk = computed(() => {
  return {
    away: moneyLineHomeRiskWin.value.win - moneyLineAwayRiskWin.value.risk,
    home: moneyLineAwayRiskWin.value.win - moneyLineHomeRiskWin.value.risk,
  };
});

const avgOddsMoneyLine = computed(() => {
  const win =
    moneylineRisk.value.home > 0
      ? moneylineRisk.value.home
      : moneylineRisk.value.away;
  const risk =
    moneylineRisk.value.home < 0
      ? moneylineRisk.value.home
      : moneylineRisk.value.away;
  const avgOdds = calculateAverageOdds(-risk, win);
  return Number.isNaN(avgOdds) ? 0 : avgOdds;
});

const moneyline1x2Risk = computed(() => {
  return {
    awayYes:
      moneyLine1x2AwayNoRiskWin.value.win -
      moneyLine1x2AwayYesRiskWin.value.risk,
    awayNo:
      moneyLine1x2AwayYesRiskWin.value.win -
      moneyLine1x2AwayNoRiskWin.value.risk,
    homeYes:
      moneyLine1x2HomeNoRiskWin.value.win -
      moneyLine1x2HomeYesRiskWin.value.risk,
    homeNo:
      moneyLine1x2HomeYesRiskWin.value.win -
      moneyLine1x2HomeNoRiskWin.value.risk,
    drawYes:
      moneyLine1x2DrawNoRiskWin.value.win -
      moneyLine1x2DrawYesRiskWin.value.risk,
    drawNo:
      moneyLine1x2DrawYesRiskWin.value.win -
      moneyLine1x2DrawNoRiskWin.value.risk,
  };
});

const avgOddsMoneyLine1x2 = computed(() => {
  const win =
    moneyline1x2Risk.value.home > 0
      ? moneyline1x2Risk.value.home
      : moneyline1x2Risk.value.away;
  const risk =
    moneyline1x2Risk.value.home < 0
      ? moneyline1x2Risk.value.home
      : moneyline1x2Risk.value.away;
  const avgOdds = calculateAverageOdds(-risk, win);
  return Number.isNaN(avgOdds) ? 0 : avgOdds;
});

const avgOddsSpreads = computed(() => {
  const win =
    spreadRisk.value.home > 0 ? spreadRisk.value.home : spreadRisk.value.away;
  const risk =
    spreadRisk.value.home < 0 ? spreadRisk.value.home : spreadRisk.value.away;
  const avgOdds = calculateAverageOdds(-risk, win);
  return Number.isNaN(avgOdds) ? 0 : avgOdds;
});

const avgOddsTotal = computed(() => {
  const win =
    totalRisk.value.over > 0 ? totalRisk.value.over : totalRisk.value.under;
  const risk =
    totalRisk.value.over < 0 ? totalRisk.value.over : totalRisk.value.under;
  const avgOdds = calculateAverageOdds(-risk, win);
  return Number.isNaN(avgOdds) ? 0 : avgOdds;
});

const totalRisk = computed(() => {
  return {
    over:
      (totalOver.value ? totalOver.value.sumWin : 0) -
      (totalUnder.value ? totalUnder.value.sumRisk : 0),
    under:
      (totalUnder.value ? totalUnder.value.sumWin : 0) -
      (totalOver.value ? totalOver.value.sumRisk : 0),
  };
});

const spreadRisk = computed(() => {
  return {
    away:
      (spreadHome.value ? spreadHome.value.sumWin : 0) -
      (spreadAway.value ? spreadAway.value.sumRisk : 0),
    home:
      (spreadAway.value ? spreadAway.value.sumWin : 0) -
      (spreadHome.value ? spreadHome.value.sumRisk : 0),
  };
});

//HOOK
onMounted(async () => {
  let payload;
  loading.value = true;
  switch (props.type) {
    case 'player':
      payload = {
        gameID: props.position.game.id,
        type: props.type,
        playerId: props.player,
      };
      break;
    default:
      payload = {
        gameID: props.position.game.id,
        type: props.type,
      };
      break;
  }
  const response = await getBetsForGame(payload);
  bets.value = response.bets;
  loading.value = false;
  if (bets.value.length === 0) {
    stage.value = 0;
  }
});

onMounted(() => {
  emitter.on('displayBet', (r) => {
    session.value = r;
    stage.value = 2;
  });
});

onBeforeUnmount(() => {
  emitter.off('displayBet');
});
//FUNCTIONS
function getSide(game, participantID) {
  return game.participants.find((p) => p.id === participantID).homeAway;
}

function groupByType(bets) {
  const moneyline = {};
  const spread = {};
  const total = {};
  const moneyline1x2 = {};

  bets.forEach((bet) => {
    let side = null;
    if (bet.type === 'moneyline') {
      side = getSide(bet.game, bet.participantID);
      if (!moneyline[side]) {
        moneyline[side] = [];
      }
      moneyline[side].push(bet);
    } else if (bet.type === 'spread') {
      side = getSide(bet.game, bet.participantID);
      if (!spread[side]) {
        spread[side] = [];
      }
      spread[side].push(bet);
    } else if (bet.type === 'total') {
      side = bet.OU;
      if (!total[side]) {
        total[side] = [];
      }
      total[side].push(bet);
    } else if (bet.type === 'moneyline1x2') {
      const sideKey = bet.side === 'yes' ? 'Yes' : 'No'; // Add optional chaining for safety
      if (bet.market !== 'draw') {
        side = bet.type;
        let homeAway = getSide(bet.game, bet.market);
        if (!moneyline1x2[homeAway + sideKey]) {
          moneyline1x2[homeAway + sideKey] = [];
        }
        moneyline1x2[homeAway + sideKey].push(bet);
      } else {
        side = bet.type;
        if (!moneyline1x2['draw' + sideKey]) {
          moneyline1x2['draw' + sideKey] = [];
        }
        moneyline1x2['draw' + sideKey].push(bet);
      }
    }
  });

  return {
    moneyline,
    spread,
    total,
    moneyline1x2,
  };
}

function oddsAvg(bets, risk) {
  const data = bets.map((bid) => ({
    win: calculateWin(bid.bet, bid.odds),
    odds: bid.odds,
  }));
  const sum =
    Number.parseFloat(risk) > 0
      ? data.reduce((acc, curr) => acc + curr.win, 0)
      : data[0].win;
  let avg;
  if (bets.length > 1) {
    avg = calculateAverageOdds(risk, sum);
  } else {
    avg = bets[0].odds;
  }
  return { avgOdds: avg, sumWin: sum, sumRisk: risk };
}

function moneyLineRiskWin(bets) {
  if (!bets) {
    return {
      risk: 0,
      win: 0,
      avgOdds: 0,
    };
  }
  const sumRisk = bets.reduce((acc, curr) => acc + curr.bet, 0);
  const avg = oddsAvg(bets, sumRisk);
  return {
    avgOdds: avg.avgOdds,
    risk: avg.sumRisk,
    win: avg.sumWin,
  };
}

function calculateAverageOdds(risk, win) {
  if (win >= risk) {
    const averageOdds = 100 * (win / risk);
    return averageOdds;
  }
  return (-100 * risk) / win;
}

function groupTotalByNumber(bets) {
  const grouped = {};
  if (!bets) {
    return null;
  }
  bets.forEach((bet) => {
    if (!grouped[bet.total]) {
      grouped[bet.total] = [];
    }
    grouped[bet.total].push(bet);
    const sum =
      Number.parseFloat(bet.bet) > 0
        ? bets.reduce((acc, curr) => acc + curr.bet, 0)
        : bets[0].bet;
    const avgOddsSum = oddsAvg(bets, sum);
    const obj = grouped[bet.total];
    Object.assign(obj, { avgOddsSum });
  });
  const keys = Object.keys(grouped);
  let sumWin = 0;
  let sumRisk = 0;
  keys.forEach((key) => {
    sumRisk += grouped[key].avgOddsSum.sumRisk;
    sumWin += grouped[key].avgOddsSum.sumWin;
  });

  Object.assign(grouped, {
    sumRisk,
    sumWin,
  });

  return grouped;
}

function groupSpreadByNumber(bets) {
  const grouped = {};
  if (!bets) {
    return null;
  }
  bets.forEach((bet) => {
    if (!grouped[bet.spread]) {
      grouped[bet.spread] = [];
    }
    grouped[bet.spread].push(bet);
    const sum =
      Number.parseFloat(bet.bet) > 0
        ? bets.reduce((acc, curr) => acc + curr.bet, 0)
        : bets[0].bet;
    const avgOddsSum = oddsAvg(bets, sum);
    const obj = grouped[bet.spread];
    Object.assign(obj, { avgOddsSum });
  });
  const keys = Object.keys(grouped);
  let sumWin = 0;
  let sumRisk = 0;
  keys.forEach((key) => {
    sumRisk += grouped[key].avgOddsSum.sumRisk;
    sumWin += grouped[key].avgOddsSum.sumWin;
  });

  Object.assign(grouped, {
    sumRisk,
    sumWin,
  });
  return grouped;
}

function calculateWin(bet, odds) {
  if (odds === 0) return 0;
  const resultWin = odds > 0 ? (bet / 100) * odds : (bet / -odds) * 100;
  if (Number.isNaN(Number.parseFloat(resultWin))) {
    return 0;
  }
  return resultWin;
}

//EMITTERS
//global
const emit = defineEmits(['show-summary', 'close-modal']);

function showSummary() {
  if (props.isPending) {
    emit('show-summary');
  } else {
    close();
  }
}

function close() {
  emit('close-modal');
}
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '@/scss/tokens.scss' as main;

.bets-not-found {
  color: main.$grey-2400;
  background: main.$grey-500;
  font-weight: main.$font-weight-semibold;
  font-size: main.$font-size-sm;
  text-align: center;
  border-radius: 4px;
}

.box-position {
  border: 1px solid main.$grey-1000;
  border-radius: 8px;
  background: main.$white;
  margin: 0;
  margin-left: -20px;
  margin-right: -20px;
}

.title-position {
  background: main.$blue-702;
  color: main.$white;
  font-size: small;
  font-weight: bold;
  padding: 8px;
  margin: 0;
  border-top-left-radius: 7px;
  border-top-right-radius: 7px;
}

.player-chart-header {
  font-weight: main.$font-weight-semibold;
  color: main.$blue-702;
  font-size: small;
  margin: 0;
  width: 100%;
  box-shadow: 0 2px 4px 0 main.$blue-1200;
  background: main.$grey-801;
  padding: 4px;
  border: none;

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    display: none;
  }
}

.spacing-vertical {
  height: 0px;

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    .spacing-vertical {
      height: 15px;
    }
  }
}

.smallText {
  font-size: xx-small;
}

.bet-list {
  border: 1px solid main.$grey-1000;
}

.button-back {
  width: 100%;
  background: main.$grey-500;
  color: main.$blue-1100;
  font-weight: bold;
}

xt {
  font-size: xx-small;
}

.bet-list {
  border: 1px solid main.$grey-1000;
}

.button-back {
  width: 100%;
  background: main.$grey-500;
  color: main.$blue-1100;
  font-weight: bold;
}

.games-not-found {
  display: block;
  align-content: center;
  color: main.$grey-2400;
  background: main.$grey-500;
  font-weight: main.$font-weight-normal;
  font-size: main.$font-size-sm;
  text-align: center;
  border-radius: 4px;
}
</style>
